import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import { NudgerRule, NudgerRuleHistoryEntry, NudgerRuleUpdateRequest } from '../../_models/admin/nudger-rule.models';

/**
 * Typed calls to the admin-only nudger rule editor (api/Maintenance/NudgerRules...). Same BaseService pattern as
 * AppSettingsService: prepareOptions() hashes the exact body sent, and responses are unwrapped.
 */
@Injectable({ providedIn: 'root' })
export class NudgerRulesService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  public getRules(): Observable<NudgerRule[]> {
    return this.http.get(this.apiUrl + 'api/Maintenance/NudgerRules', this.prepareOptions(null)).pipe(
      map((data: any) => (this.unwrap(data) || []) as NudgerRule[]),
      catchError(this.handleError('getNudgerRules'))
    );
  }

  /** Saves one rule's full editable state and returns the rule as it now stands. */
  public saveRule(workFlowType: number, request: NudgerRuleUpdateRequest): Observable<NudgerRule> {
    return this.http.put(
      this.apiUrl + 'api/Maintenance/NudgerRules/' + encodeURIComponent(String(workFlowType)),
      request,
      this.prepareOptions(request)
    ).pipe(
      map((data: any) => this.unwrap(data) as NudgerRule),
      catchError(this.handleError('saveNudgerRule'))
    );
  }

  /** The latest 50 changes to one rule, newest first. */
  public getHistory(workFlowType: number): Observable<NudgerRuleHistoryEntry[]> {
    return this.http.get(
      this.apiUrl + 'api/Maintenance/NudgerRules/' + encodeURIComponent(String(workFlowType)) + '/history',
      this.prepareOptions(null)
    ).pipe(
      map((data: any) => (this.unwrap(data) || []) as NudgerRuleHistoryEntry[]),
      catchError(this.handleError('getNudgerRuleHistory'))
    );
  }

  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
